/**
 * Sorting by a drag handle (the grip ≡, iOS calls it the reorder control): press it, move, let go.
 * Pointer events, so mouse, finger and pen work the same; HTML5 drag and drop does not on the
 * iPhone. Every gap between rows is a place to drop, also after the last row and in an empty group;
 * the nearest gap wins. Near the top or bottom of the window the page scrolls along. The handle
 * also moves its row with the arrow keys.
 *
 * Markup: a root with `data-sort-root`, groups (sections) with `data-sort-group="<id>"`, rows inside
 * them with `data-sort-row`, in order.
 */
import { useEffect, useRef, useState } from "react";
import type { KeyboardEvent, PointerEvent } from "react";

/** A place in a group: before the row with this index (the length: at the end). */
export interface Slot {
  group: string;
  index: number;
}

interface Gap {
  slot: Slot;
  /** In page coordinates. */
  y: number;
}

/** Where a row lands in its new group once it is taken out of the old one; null: it stays. */
export function landing(from: Slot, to: Slot): Slot | null {
  if (to.group !== from.group) return to;
  if (to.index === from.index || to.index === from.index + 1) return null;
  return { group: to.group, index: to.index > from.index ? to.index - 1 : to.index };
}

/** The gaps of a sortable list, measured on the page. */
function gapsOf(root: HTMLElement): Gap[] {
  const gaps: Gap[] = [];
  const top = (r: DOMRect) => r.top + window.scrollY;
  for (const box of root.querySelectorAll<HTMLElement>("[data-sort-group]")) {
    const group = box.dataset.sortGroup!;
    const rows = [...box.querySelectorAll<HTMLElement>("[data-sort-row]")].filter((r) => r.closest("[data-sort-group]") === box).map((r) => r.getBoundingClientRect());
    if (rows.length === 0) {
      const r = box.getBoundingClientRect();
      gaps.push({ slot: { group, index: 0 }, y: top(r) + r.height / 2 });
      continue;
    }
    rows.forEach((r, i) => gaps.push({ slot: { group, index: i }, y: i === 0 ? top(r) : (top(rows[i - 1]!) + rows[i - 1]!.height + top(r)) / 2 }));
    const last = rows[rows.length - 1]!;
    gaps.push({ slot: { group, index: rows.length }, y: top(last) + last.height });
  }
  return gaps;
}

const EDGE = 72;
const SPEED = 14;

/**
 * `move(from, to)` gets the place the row lands (`landing`), only when it moves. `step(from, by)`
 * moves it by the arrow keys. While dragging, `target` is the gap it would drop into.
 */
export function useSortable(move: (from: Slot, to: Slot) => void, step: (from: Slot, by: -1 | 1) => void) {
  const [dragging, setDragging] = useState<{ from: Slot; target: Slot | null } | null>(null);
  const live = useRef<{ from: Slot; row: HTMLElement; startY: number; startScroll: number; clientY: number; gaps: Gap[]; target: Slot | null; frame: number } | null>(null);
  const moveRef = useRef(move);
  moveRef.current = move;

  const place = () => {
    const d = live.current;
    if (!d) return;
    d.row.style.transform = `translateY(${d.clientY - d.startY + window.scrollY - d.startScroll}px)`;
    const y = d.clientY + window.scrollY;
    let best: Gap | null = null;
    for (const g of d.gaps) if (!best || Math.abs(g.y - y) < Math.abs(best.y - y)) best = g;
    const target = best?.slot ?? null;
    if (target?.group !== d.target?.group || target?.index !== d.target?.index) {
      d.target = target;
      setDragging({ from: d.from, target });
    }
  };

  // Scrolls while the pointer stays near an edge of the window.
  const scroll = () => {
    const d = live.current;
    if (!d) return;
    const by = d.clientY < EDGE ? -SPEED : d.clientY > window.innerHeight - EDGE - 56 ? SPEED : 0;
    if (by) {
      window.scrollBy(0, by);
      place();
    }
    d.frame = requestAnimationFrame(scroll);
  };

  const end = (drop: boolean) => {
    const d = live.current;
    if (!d) return;
    cancelAnimationFrame(d.frame);
    d.row.style.transform = "";
    d.row.classList.remove("lifted");
    live.current = null;
    setDragging(null);
    const to = drop && d.target ? landing(d.from, d.target) : null;
    if (to) moveRef.current(d.from, to);
  };
  useEffect(() => () => void (live.current && cancelAnimationFrame(live.current.frame)), []);

  const handle = (from: Slot, label: string) => ({
    role: "button",
    tabIndex: 0,
    "aria-label": `${label} verschieben (ziehen oder Pfeiltasten)`,
    title: "Zum Verschieben ziehen",
    className: "grip",
    onPointerDown: (e: PointerEvent<HTMLElement>) => {
      if (e.button !== 0) return;
      const row = e.currentTarget.closest<HTMLElement>("[data-sort-row]");
      const root = e.currentTarget.closest<HTMLElement>("[data-sort-root]");
      if (!row || !root) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      row.classList.add("lifted");
      live.current = { from, row, startY: e.clientY, startScroll: window.scrollY, clientY: e.clientY, gaps: gapsOf(root), target: null, frame: 0 };
      live.current.frame = requestAnimationFrame(scroll);
      setDragging({ from, target: null });
    },
    onPointerMove: (e: PointerEvent<HTMLElement>) => {
      if (!live.current) return;
      live.current.clientY = e.clientY;
      place();
    },
    onPointerUp: () => end(true),
    onPointerCancel: () => end(false),
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        e.preventDefault();
        step(from, e.key === "ArrowUp" ? -1 : 1);
      }
    },
  });

  /** Classes for a row and for a group: the line where the row would drop. */
  const rowClass = (slot: Slot) => {
    const t = dragging?.target;
    const lands = dragging && t ? landing(dragging.from, t) !== null : false;
    return lands && t && t.group === slot.group && t.index === slot.index ? "drop-before" : "";
  };
  const groupClass = (group: string, length: number) => {
    const t = dragging?.target;
    const lands = dragging && t ? landing(dragging.from, t) !== null : false;
    return lands && t && t.group === group && t.index === length ? (length === 0 ? "drop-empty" : "drop-end") : "";
  };

  return { dragging, handle, rowClass, groupClass };
}

/** The grip: three lines, as on the iPhone. */
export const GRIP_PATH = "M4 7h16M4 12h16M4 17h16";
